    import React from 'react';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Banner shown in the conversation header when an incoming phone number
 * matches an existing customer. Allows the user to confirm the link or dismiss.
 */
export default function CustomerMatchBanner({
  conversationSid,
  suggestedCustomer,
  onLinked,
  onDismissed,
}) {
  const [loading, setLoading] = React.useState(null); // 'link' | 'dismiss' | null

  const handleLink = async () => {
    setLoading('link');
    try {
      const res = await fetch(`${API_URL}/conversations/${conversationSid}/link-customer`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          customer_id: suggestedCustomer.id,
          customer_name: suggestedCustomer.name,
        }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to link customer');
      }

      onLinked(suggestedCustomer);
    } catch (error) {
      console.error('Failed to link customer:', error);
      alert(error.message || 'Failed to link customer');
    } finally {
      setLoading(null);
    }
  };

  const handleDismiss = async () => {
    setLoading('dismiss');
    try {
      const res = await fetch(`${API_URL}/conversations/${conversationSid}/dismiss-suggestion`, {
        method: 'PATCH',
        credentials: 'include',
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to dismiss suggestion');
      }

      onDismissed();
    } catch (error) {
      console.error('Failed to dismiss suggestion:', error);
      alert(error.message || 'Failed to dismiss suggestion');
    } finally {
      setLoading(null);
    }
  };

  const displayPhone = suggestedCustomer.mobile_phone
    || suggestedCustomer.home_phone
    || suggestedCustomer.work_phone
    || '';

  return (
    <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 mb-3">
      <div className="flex items-center gap-3 flex-1 min-w-0">
        {/* Icon */}
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-amber-500" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>

        {/* Message */}
        <div className="flex-1 min-w-0">
          <p className="text-sm text-amber-800">
            This number matches{' '}
            <span className="font-semibold">{suggestedCustomer.name}</span>
            {displayPhone && (
              <span className="text-amber-600 ml-1">({displayPhone})</span>
            )}
          </p>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center gap-2 flex-shrink-0 ml-3">
        <button
          onClick={handleDismiss}
          disabled={loading !== null}
          className="text-xs px-3 py-1.5 rounded-md font-medium text-gray-600 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading === 'dismiss' ? '...' : 'Dismiss'}
        </button>
        <button
          onClick={handleLink}
          disabled={loading !== null}
          className="text-xs px-3 py-1.5 rounded-md font-medium bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading === 'link' ? 'Linking...' : 'Link Customer'}
        </button>
      </div>
    </div>
  );
}
